


import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { messagesAPI, resourcesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Messages.css';

const Messages = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [resources, setResources] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const didAutoOpen = useRef(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    setLoadingConvs(true);
    try {
      const { data } = await messagesAPI.getConversations();
      setConversations(data);
    } catch {}
    finally { setLoadingConvs(false); }
  };

  const openConversation = useCallback(async (conv) => {
    setActiveConv(conv);
    setLoadingMsgs(true);
    try {
      const { data } = await messagesAPI.getMessages(conv.user._id);
      setMessages(data);
      setConversations(cs => cs.map(c => c.user._id === conv.user._id ? { ...c, unread: 0 } : c));
    } catch {}
    finally { setLoadingMsgs(false); }
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    const targetId = searchParams.get('userId');
    const targetName = searchParams.get('name');
    if (!targetId || didAutoOpen.current || loadingConvs) return;
    didAutoOpen.current = true;

    const existing = conversations.find(c => c.user._id === targetId);
    if (existing) {
      openConversation(existing);
    } else {
      const syntheticConv = { user: { _id: targetId, name: decodeURIComponent(targetName || 'User'), avatar: '' }, lastMessage: null, unread: 0 };
      setConversations(cs => {
        const already = cs.find(c => c.user._id === targetId);
        return already ? cs : [syntheticConv, ...cs];
      });
      openConversation(syntheticConv);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, conversations, loadingConvs, openConversation, setSearchParams]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      const { data } = await messagesAPI.send({ receiverId: activeConv.user._id, content: newMsg.trim() });
      setMessages(m => [...m, data]);
      setNewMsg('');
      fetchConversations();
    } catch {} finally { setSending(false); }
  };

  const handleShareResource = async () => {
    try {
      const { data } = await resourcesAPI.getAll();
      setResources(data.filter(r => r.uploader?._id === user._id));
      setShowShare(true);
    } catch {}
  };

  const sendResource = async (resource) => {
    if (!activeConv) return;
    try {
      const content = resource.type === 'link'
        ? `📎 Resource: ${resource.title} — ${resource.content}`
        : `📎 Resource: ${resource.title} (${resource.skill})`;
      const { data } = await messagesAPI.send({ receiverId: activeConv.user._id, content, type: 'resource' });
      setMessages(m => [...m, data]);
      if (resource.uploader?._id === user._id) {
        await resourcesAPI.share(resource._id, activeConv.user._id);
      }
      setShowShare(false);
    } catch {}
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday
      ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const initials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="messages-page">
      <div className="messages-layout">
        <aside className="conversations-panel">
          <div className="conv-header">
            <h2>Messages</h2>
          </div>
          {loadingConvs ? (
            <div style={{ padding: 20 }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ height: 64, borderRadius: 8, background: 'var(--gray-100)', marginBottom: 8, animation: 'shimmer 1.4s infinite' }} />
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 20px' }}>
              <div className="empty-state-icon">💬</div>
              <h3>No messages yet</h3>
              <p>Connect with someone to start chatting</p>
            </div>
          ) : (
            <div className="conv-list">
              {conversations.map(conv => (
                <button
                  key={conv.user._id}
                  className={`conv-item ${activeConv?.user._id === conv.user._id ? 'active' : ''}`}
                  onClick={() => openConversation(conv)}
                >
                  <div className="avatar" style={{ width: 44, height: 44, fontSize: 15, flexShrink: 0 }}>
                    {conv.user.avatar
                      ? <img src={conv.user.avatar} alt={conv.user.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                      : initials(conv.user.name)}
                  </div>
                  <div className="conv-info">
                    <div className="conv-name-row">
                      <span className="conv-name">{conv.user.name}</span>
                      <span className="conv-time">{formatTime(conv.lastMessage?.createdAt)}</span>
                    </div>
                    <p className="conv-preview">
                      {conv.lastMessage?.content?.slice(0, 50)}{conv.lastMessage?.content?.length > 50 ? '…' : ''}
                    </p>
                  </div>
                  {conv.unread > 0 && (
                    <span className="unread-badge">{conv.unread}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </aside>

        <main className="chat-panel">
          {!activeConv ? (
            <div className="chat-empty">
              <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
              <h3>Select a conversation</h3>
              <p>Choose someone from the left to start chatting</p>
            </div>
          ) : (
            <>
              <div className="chat-header">
                <div className="avatar" style={{ width: 38, height: 38, fontSize: 14 }}>
                  {activeConv.user.avatar
                    ? <img src={activeConv.user.avatar} alt={activeConv.user.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                    : initials(activeConv.user.name)}
                </div>
                <div>
                  <p className="chat-header-name">{activeConv.user.name}</p>
                  <p className="chat-header-sub">{activeConv.user.email}</p>
                </div>
              </div>

              <div className="chat-messages">
                {loadingMsgs ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                    <div className="loading-spinner loading-spinner-dark" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="chat-no-messages">
                    <p>Start your conversation with {activeConv.user.name}</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, i) => {
                      const isMe = msg.sender?._id === user?._id || msg.sender === user?._id;
                      const showDate = i === 0 || new Date(msg.createdAt).toDateString() !== new Date(messages[i-1].createdAt).toDateString();
                      return (
                        <div key={msg._id}>
                          {showDate && (
                            <div className="chat-date-divider">
                              <span>{new Date(msg.createdAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                            </div>
                          )}
                          <div className={`message-row ${isMe ? 'me' : 'them'}`}>
                            {!isMe && (
                              <div className="avatar" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
                                {initials(activeConv.user.name)}
                              </div>
                            )}
                            <div className={`message-bubble ${isMe ? 'me' : 'them'} ${msg.type === 'resource' ? 'resource-msg' : ''}`}>
                              {msg.content}
                              <span className="msg-time">{formatTime(msg.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div className="chat-input-bar">
                <button className="attach-btn" onClick={handleShareResource} title="Share resource">📎</button>
                <form className="chat-form" onSubmit={sendMessage}>
                  <input
                    ref={inputRef}
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    placeholder={`Message ${activeConv.user.name}…`}
                    style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  />
                  <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !newMsg.trim()}>
                    {sending ? <span className="loading-spinner" /> : '→'}
                  </button>
                </form>
              </div>
            </>
          )}
        </main>
      </div>

      {showShare && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowShare(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>Share a Resource</h3>
              <button className="close-btn" onClick={() => setShowShare(false)}>✕</button>
            </div>
            <div className="modal-body">
              {resources.length === 0 ? (
                <div className="empty-state">
                  <p>You don't have any resources to share. Add some in your Profile page.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {resources.map(r => (
                    <button
                      key={r._id}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--gray-50)', border: '1.5px solid var(--gray-100)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                      onClick={() => sendResource(r)}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-light)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-100)'; e.currentTarget.style.background = 'var(--gray-50)'; }}
                    >
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--gray-800)', marginBottom: 4 }}>{r.title}</p>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <span className="badge badge-primary">{r.skill}</span>
                          <span className="badge badge-gray">{r.type}</span>
                        </div>
                      </div>
                      <span style={{ color: 'var(--primary)' }}>Share →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;